import { X } from "lucide-react";
import { useSoilUnits } from "../../stores/soilUnitsStore";
import {
  MAX_DISPLAY_DEPTH_IN,
  MIN_DISPLAY_DEPTH_IN,
  isDefaultUnits,
  K_DISPLAY_OPTIONS,
  P_DISPLAY_OPTIONS,
  STORED_DEPTH_IN,
  MASS_BASIS_OPTIONS,
} from "../../lib/soilUnits";
import { Modal } from "../../ui/Modal";

// Stored soil values stay Mehlich-3 at 8 inches. This is a display lens applied when a number
// becomes text, never an input to arithmetic. Every factor is the ingest conversion read backwards
// a missing factor shows the stored value labelled M3.
export function SoilUnitsPanel({ onClose }: { onClose: () => void }) {
  const depthIn = useSoilUnits((s) => s.depthIn);
  const source = useSoilUnits((s) => s.source);
  const pExtractant = useSoilUnits((s) => s.pExtractant);
  const kExtractant = useSoilUnits((s) => s.kExtractant);
  const massBasis = useSoilUnits((s) => s.massBasis);
  const setUnits = useSoilUnits((s) => s.set);
  const reset = useSoilUnits((s) => s.reset);
  const atDefault = isDefaultUnits({
    depthIn,
    pExtractant,
    kExtractant,
    massBasis,
  });

  const row = <T extends string>(
    label: string,
    value: T,
    opts: { value: T; label: string }[],
    onPick: (v: T) => void,
  ) => (
    <div className="mt-2">
      <div className="text-[11px] font-medium text-muted">{label}</div>
      <div className="mt-1 flex flex-wrap gap-1">
        {opts.map((o) => {
          const on = value === o.value;
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={on}
              onClick={() => onPick(o.value)}
              className={`rounded border px-2 py-1 text-[11px] ${
                on
                  ? "border-accent bg-accent text-canvas"
                  : "border-line text-muted hover:bg-hover hover:text-fg"
              }`}
            >
              {/* The "≈ M3" annotation was REMOVED: ruled the identities away, so it became a false claim on screen for both Bray and Ammonium-Acetate. */}
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <Modal>
      <div
        className="fixed inset-0 z-dialog flex items-start justify-center bg-black/50 p-4 sm:p-8"
        onClick={onClose}
      >
        <div
          className="w-full max-w-md rounded-lg border border-line bg-surface p-3 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <h2 className="text-sm font-medium text-fg">Soil data units</h2>
              <p className="mt-0.5 text-[11px] text-faint">
                How you want to read soil values. This changes what’s displayed
                — never what’s stored or how anything is calculated.
              </p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="rounded p-1 text-faint hover:bg-hover hover:text-fg"
            >
              <X size={14} />
            </button>
          </div>

          {source === "org" && (
            // transparency: a member reading through the ORG's default should know and know that
            // touching any control below makes the choice their own.
            <p className="mt-2 rounded bg-accent/10 px-2 py-1 text-[10px] text-accent">
              Using your organization’s default units. Change anything below to
              make your own choice.
            </p>
          )}
          <div className="mt-2">
            <div className="text-[11px] font-medium text-muted">
              Sample depth
            </div>
            <div className="mt-1 flex items-center gap-1.5">
              <input
                type="number"
                step="0.5"
                min={MIN_DISPLAY_DEPTH_IN}
                max={MAX_DISPLAY_DEPTH_IN}
                value={depthIn}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (
                    Number.isFinite(v) &&
                    v >= MIN_DISPLAY_DEPTH_IN &&
                    v <= MAX_DISPLAY_DEPTH_IN
                  )
                    setUnits({ depthIn: v });
                }}
                aria-label="Display sample depth in inches"
                className="w-20 rounded border border-line bg-transparent px-1.5 py-1 text-[11px] outline-none focus:border-accent"
              />
              <span className="text-[11px] text-faint">
                inches ({MIN_DISPLAY_DEPTH_IN}–{MAX_DISPLAY_DEPTH_IN})
              </span>
            </div>
            {depthIn !== STORED_DEPTH_IN && (
              // No "still": the colour/number rank divergence was ruled ACCEPTED PERMANENTLY on, so
              // the copy must not imply a fix is coming.
              <p className="mt-1 text-[10px] text-amber-500">
                Soil map numbers are estimated at {depthIn}″ — the hover
                readings, the high/low pins and the legend. The map’s{" "}
                <em>colours</em> shade by {STORED_DEPTH_IN}″ values, so a cell’s
                colour and its number can rank differently.
              </p>
            )}
          </div>

          {row("Phosphorus", pExtractant, P_DISPLAY_OPTIONS, (v) =>
            setUnits({ pExtractant: v }),
          )}
          {row("Potassium / micros", kExtractant, K_DISPLAY_OPTIONS, (v) =>
            setUnits({ kExtractant: v }),
          )}
          {row("Units", massBasis, MASS_BASIS_OPTIONS, (v) =>
            setUnits({ massBasis: v }),
          )}
          {massBasis === "lbs_ac" && (
            // The factor is depth-dependent and saying so heads off the obvious cross-check failure: someone
            // multiplying a ppm by a remembered 2.4 while the panel is set to 6" gets a different number and
            // reports a bug that is actually arithmetic.
            <p className="mt-1 text-[10px] text-faint">
              lbs/acre of the {depthIn}″ furrow slice — ppm ×{" "}
              {(0.3 * depthIn).toFixed(2)}. The factor scales with the sample
              depth above, because a shallower slice holds less soil.
            </p>
          )}

          <p className="mt-3 border-t border-line pt-2 text-[10px] text-faint">
            The system always stores and computes in {STORED_DEPTH_IN}″
            Mehlich-3 — every model, index and recommendation is calibrated to
            it. Converted values are labelled wherever they appear, and
            fertilizer recommendations stay in Mehlich-3 terms. Every extractant
            moves the numbers — Bray-1 and Olsen alike for P, and
            ammonium-acetate for K, Ca and Mg (each by its own factor, not one
            shared one). A depth change is a real, soil-dependent shift — the
            same 150 ppm K can read ~123 or ~256 at 6″ depending on the soil —
            because it’s what a core to that depth would actually test, not a
            rescaling.
          </p>

          {!atDefault && (
            <button
              onClick={reset}
              className="mt-2 rounded border border-line px-2 py-1 text-[11px] text-muted hover:bg-hover hover:text-fg"
            >
              Reset to {STORED_DEPTH_IN}″ · Mehlich-3
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
